import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Row, Col, Card, CardBody, Badge, Button, Input, Collapse, Spinner, Alert,
} from 'reactstrap';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { draftRawToHtml } from 'util/draftToHtml';
import { fetchMissedQuestions } from 'redux/QuestionBank/TestProgressAction';
import { fetchQuestionsByIds } from 'redux/QuestionBank/QuestionsAction';

// Question content is authored by admins, but it still reaches the DOM as raw
// HTML — sanitize before rendering rather than trusting the source.
const safeHtml = (value) => {
  const html = typeof value === 'string' ? value : draftRawToHtml(value);
  return DOMPurify.sanitize(html || '');
};

const plainText = (content) => {
  if (!content) return '';
  if (typeof content === 'string') return content;
  if (content.blocks) return content.blocks.map((b) => b.text).join(' ');
  return '';
};

// The cache stores terse field names so the whole map fits in one Firestore
// document — expand them here so the rest of the component stays readable.
const expandStats = (raw) => ({
  timesMissed: raw.m || 0,
  timesOmitted: raw.o || 0,
  timesCorrect: raw.c || 0,
  firstMissedAt: raw.f || null,
  lastMissedAt: raw.l || null,
  lastAnsweredAt: raw.a || null,
  currentlyCorrect: !!raw.cc,
  isMarked: !!raw.k,
  lastSelectedChoiceId: raw.s || null,
});

const ITEMS_PER_PAGE = 10;

const MissedQuestionsPanel = () => {
  const dispatch = useDispatch();

  const { missedQuestions, missedLoading, missedError, missedLoaded, missedSpecialty } =
    useSelector((state) => state.testProgress);
  const { subjects } = useSelector((state) => state.questionBankSubjects);
  const activeSpecialty = useSelector((state) => state.specialty.activeSpecialty);

  const [questionsMap, setQuestionsMap] = useState({});
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  // Per-question answer distribution, fetched only for questions actually opened
  const [distributions, setDistributions] = useState({});

  const handleToggleExpand = async (questionId) => {
    if (expandedId === questionId) {
      setExpandedId(null);
      return;
    }

    setExpandedId(questionId);

    if (distributions[questionId] !== undefined) return;

    try {
      // Specialty must be sent — the backend defaults to pmr without it
      const res = await axios.post('/fetchQuestionAnswerDistribution', {
        questionId,
        specialty: activeSpecialty,
      });
      setDistributions((prev) => ({ ...prev, [questionId]: res.data }));
    } catch (err) {
      // Percentages are a nice-to-have — never block reviewing the question
      setDistributions((prev) => ({ ...prev, [questionId]: null }));
    }
  };

  const [searchQuery, setSearchQuery] = useState('');
  const [filterSubject, setFilterSubject] = useState('');
  const [showFixed, setShowFixed] = useState(false);
  const [showOmitted, setShowOmitted] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  // Load on first open, and again whenever the specialty changes — cached data
  // belongs to whichever specialty it was fetched for
  useEffect(() => {
    const staleSpecialty = missedLoaded && missedSpecialty !== activeSpecialty;
    if (!missedLoading && (!missedLoaded || staleSpecialty)) {
      if (staleSpecialty) {
        setQuestionsMap({});
        setDistributions({});
      }
      dispatch(fetchMissedQuestions());
    }
  }, [dispatch, missedLoaded, missedLoading, missedSpecialty, activeSpecialty]);

  // Pull the question content for whatever ids came back
  useEffect(() => {
    let cancelled = false;
    const ids = Object.keys(missedQuestions || {});
    if (ids.length === 0) return undefined;

    const load = async () => {
      setLoadingQuestions(true);
      const result = await dispatch(fetchQuestionsByIds(ids));
      if (!cancelled) {
        setQuestionsMap(result?.questions || {});
        setLoadingQuestions(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [dispatch, missedQuestions]);

  const rows = useMemo(() => {
    const entries = Object.entries(missedQuestions || {});

    return entries
      .map(([questionId, raw]) => ({
        questionId,
        stats: expandStats(raw),
        question: questionsMap[questionId] || null,
      }))
      // A question can be archived after being answered — skip rather than crash
      .filter((row) => row.question)
      .filter((row) => {
        if (!showFixed && row.stats.currentlyCorrect) return false;

        // Omitted-only entries are those never actually answered wrong
        const omittedOnly = (row.stats.timesMissed || 0) === 0 && (row.stats.timesOmitted || 0) > 0;
        if (!showOmitted && omittedOnly) return false;

        if (filterSubject && row.question.subjectId !== filterSubject) return false;

        if (searchQuery.trim()) {
          const haystack = `${plainText(row.question.questionText)} ${row.question.questionCode || ''}`.toLowerCase();
          if (!haystack.includes(searchQuery.trim().toLowerCase())) return false;
        }

        return true;
      })
      .sort((a, b) => (b.stats.lastMissedAt || 0) - (a.stats.lastMissedAt || 0));
  }, [missedQuestions, questionsMap, showFixed, showOmitted, filterSubject, searchQuery]);

  const totals = useMemo(() => {
    const all = Object.values(missedQuestions || {});
    return {
      total: all.length,
      stillWrong: all.filter((s) => !s.cc).length,
      fixed: all.filter((s) => s.cc).length,
    };
  }, [missedQuestions]);

  // Same pagination shape as the tests list on this page
  const totalPages = Math.ceil(rows.length / ITEMS_PER_PAGE);
  const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
  const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
  const currentRows = rows.slice(indexOfFirstItem, indexOfLastItem);

  // Filters changing can shrink the list beneath the current page
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterSubject, showFixed, showOmitted]);

  if (missedLoading || loadingQuestions) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" />
        <p className="text-muted mt-3 mb-0">Gathering everything you've missed…</p>
      </div>
    );
  }

  if (missedError) {
    return <Alert color="danger">Could not load your missed questions: {missedError}</Alert>;
  }

  if (totals.total === 0) {
    return (
      <div className="text-center py-5 text-muted">
        <i className="fas fa-check-circle fa-3x text-success mb-3" />
        <h4>Nothing missed yet</h4>
        <p className="mb-0">Questions you get wrong or leave blank will collect here.</p>
      </div>
    );
  }

  return (
    <>
      <Row className="mb-3">
        <Col md="4" className="mb-2 mb-md-0">
          <Card className="bg-gradient-danger shadow-sm">
            <CardBody className="py-3 text-white">
              <div className="h2 mb-0 text-white">{totals.stillWrong}</div>
              <small>Still getting wrong</small>
            </CardBody>
          </Card>
        </Col>
        <Col md="4" className="mb-2 mb-md-0">
          <Card className="bg-gradient-success shadow-sm">
            <CardBody className="py-3 text-white">
              <div className="h2 mb-0 text-white">{totals.fixed}</div>
              <small>Since fixed</small>
            </CardBody>
          </Card>
        </Col>
        <Col md="4">
          <Card className="bg-gradient-primary shadow-sm">
            <CardBody className="py-3 text-white">
              <div className="h2 mb-0 text-white">{totals.total}</div>
              <small>Ever missed</small>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md="5" className="mb-2 mb-md-0">
          <Input
            type="text"
            placeholder="Search question text or code..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </Col>
        <Col md="3" className="mb-2 mb-md-0">
          <Input
            type="select"
            value={filterSubject}
            onChange={(e) => setFilterSubject(e.target.value)}
          >
            <option value="">All subjects</option>
            {(subjects || []).map((s) => (
              <option key={s.subjectId} value={s.subjectId}>{s.subjectName || s.name}</option>
            ))}
          </Input>
        </Col>
        <Col md="4" className="d-flex align-items-center">
          <div className="custom-control custom-checkbox mr-3">
            <input
              className="custom-control-input"
              id="missed-show-fixed"
              type="checkbox"
              checked={showFixed}
              onChange={(e) => setShowFixed(e.target.checked)}
            />
            <label className="custom-control-label" htmlFor="missed-show-fixed">
              <small>Include fixed</small>
            </label>
          </div>
          <div className="custom-control custom-checkbox">
            <input
              className="custom-control-input"
              id="missed-show-omitted"
              type="checkbox"
              checked={showOmitted}
              onChange={(e) => setShowOmitted(e.target.checked)}
            />
            <label className="custom-control-label" htmlFor="missed-show-omitted">
              <small>Include omitted</small>
            </label>
          </div>
        </Col>
      </Row>

      {rows.length === 0 ? (
        <div className="text-center py-5 text-muted">
          No questions match these filters.
        </div>
      ) : (
        currentRows.map(({ questionId, stats, question }) => {
          const isOpen = expandedId === questionId;
          const timesMissed = stats.timesMissed || 0;
          const timesOmitted = stats.timesOmitted || 0;

          return (
            <Card key={questionId} className="shadow-sm mb-2">
              <CardBody className="py-3">
                <div
                  className="d-flex justify-content-between align-items-start"
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleToggleExpand(questionId)}
                >
                  <div className="mr-3">
                    <div className="mb-1">
                      {stats.currentlyCorrect ? (
                        <Badge color="success" pill className="mr-1">Fixed</Badge>
                      ) : (
                        <Badge color="danger" pill className="mr-1">Still wrong</Badge>
                      )}
                      {timesMissed > 0 && (
                        <Badge color="light" pill className="mr-1">
                          Missed {timesMissed}&times;
                        </Badge>
                      )}
                      {timesOmitted > 0 && (
                        <Badge color="warning" pill className="mr-1">
                          Omitted {timesOmitted}&times;
                        </Badge>
                      )}
                      {stats.isMarked && <i className="fas fa-flag text-warning ml-1" />}
                    </div>
                    {isOpen ? (
                      <div
                        style={{ fontSize: '0.9rem', color: '#525f7f' }}
                        dangerouslySetInnerHTML={{ __html: safeHtml(question.questionText) }}
                      />
                    ) : (
                      <div style={{ fontSize: '0.9rem', color: '#525f7f' }}>
                        {`${plainText(question.questionText).slice(0, 160)}…`}
                      </div>
                    )}
                  </div>
                  <Button color="link" size="sm" className="p-0">
                    <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`} />
                  </Button>
                </div>

                <Collapse isOpen={isOpen}>
                  <hr className="my-3" />
                  {stats.lastSelectedChoiceId === null && (
                    <Alert color="warning" className="py-2">
                      <small>You left this one blank.</small>
                    </Alert>
                  )}

                  {(question.choices || []).map((choice, index) => {
                    const isCorrect = (question.correctAnswer || []).includes(choice.choiceId);
                    const isChosen = stats.lastSelectedChoiceId === choice.choiceId;

                    // Same calculation and 5-response threshold as the test view
                    const dist = distributions[questionId];
                    const distMap = dist?.answerDistribution || {};
                    const totalResponses = Object.values(distMap)
                      .reduce((sum, entry) => sum + (entry?.count || 0), 0);
                    const chosenCount = distMap[choice.choiceId]?.count || 0;
                    const chosenPercentage = totalResponses > 0
                      ? Math.round((chosenCount / totalResponses) * 100)
                      : 0;
                    const showPercent = totalResponses >= 5;
                    // Their pick is only wrong when it isn't also the correct one
                    const isWrongPick = isChosen && !isCorrect;

                    const borderClass = isCorrect
                      ? 'border-success'
                      : (isWrongPick ? 'border-danger' : '');
                    const background = isCorrect
                      ? 'rgba(40, 167, 69, 0.1)'
                      : (isWrongPick ? 'rgba(220, 53, 69, 0.1)' : 'transparent');
                    const markerClass = isCorrect
                      ? 'bg-success text-white'
                      : (isWrongPick ? 'bg-danger text-white' : 'bg-light text-dark');

                    return (
                      <div
                        key={choice.choiceId}
                        className={`p-2 mb-2 rounded border ${borderClass}`}
                        style={{ backgroundColor: background }}
                      >
                        <div className="d-flex align-items-start">
                          <div
                            className={`rounded-circle d-flex align-items-center justify-content-center mr-2 ${markerClass}`}
                            style={{ width: '28px', height: '28px', minWidth: '28px', fontSize: '0.85rem' }}
                          >
                            {isCorrect ? <i className="fas fa-check" /> : (isWrongPick ? <i className="fas fa-times" /> : String.fromCharCode(65 + index))}
                          </div>
                          <div className="flex-grow-1">
                            <div
                              style={{ fontSize: '0.9rem' }}
                              dangerouslySetInnerHTML={{ __html: safeHtml(choice.text) }}
                            />
                            <div className="mt-1 d-flex align-items-center flex-wrap">
                              {isChosen && (
                                <Badge color={isCorrect ? 'success' : 'danger'} pill className="mr-2">
                                  Your answer
                                </Badge>
                              )}
                              {showPercent && (
                                <span
                                  className={isCorrect ? 'text-success' : 'text-muted'}
                                  style={{ fontSize: '0.75rem', fontWeight: '500', whiteSpace: 'nowrap' }}
                                >
                                  <i className="fas fa-users mr-1"></i>
                                  {chosenPercentage}%
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {question.explanationText && (
                    <div
                      className="mt-3 p-3 rounded"
                      style={{ backgroundColor: '#e8f4f8', borderLeft: '3px solid #1e6091' }}
                    >
                      <div style={{ fontWeight: 500, color: '#1e6091', marginBottom: '4px' }}>
                        Explanation
                      </div>
                      <div
                        style={{ fontSize: '0.9rem', color: '#333' }}
                        dangerouslySetInnerHTML={{ __html: safeHtml(question.explanationText) }}
                      />
                    </div>
                  )}
                </Collapse>
              </CardBody>
            </Card>
          );
        })
      )}

      {rows.length > 0 && (
        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center pt-3">
          <div className="text-muted small mb-2 mb-sm-0">
            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, rows.length)} of {rows.length} questions
            {rows.length !== totals.total && ` (filtered from ${totals.total} total)`}
          </div>
          <div className="d-flex align-items-center">
            <Button
              color="primary"
              size="sm"
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1 || totalPages <= 1}
              className="mr-2"
            >
              <i className="fas fa-chevron-left"></i>
              <span className="d-none d-sm-inline ml-1">Previous</span>
            </Button>
            <span className="mx-2 text-nowrap">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              color="primary"
              size="sm"
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages || totalPages <= 1}
              className="ml-2"
            >
              <span className="d-none d-sm-inline mr-1">Next</span>
              <i className="fas fa-chevron-right"></i>
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default MissedQuestionsPanel;
